import { useState, useCallback, useMemo } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Codes } from '@/constants/codes';

interface CaseReportOffendersListProps {
  offenders: any[];
  involvedPersons?: any[];
  isReadOnly?: boolean;
  onChange?: (offenders: any[]) => void;
  onAdd?: () => void;
  onEdit?: (offender: any) => void;
}

export function CaseReportOffendersList({
  offenders,
  involvedPersons = [],
  isReadOnly = false,
  onChange,
  onAdd,
  onEdit,
}: CaseReportOffendersListProps) {
  const { theme } = useTheme();

  const [expandedIds, setExpandedIds] = useState<string[]>([]);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; item: any }>({
    open: false,
    item: null,
  });

  const hasOffenders = useMemo(() => {
    return offenders && offenders.length > 0;
  }, [offenders]);

  const addOffender = useCallback(() => {
    onAdd?.();
  }, [onAdd]);

  const editOffender = useCallback((offender: any) => {
    onEdit?.(offender);
  }, [onEdit]);

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const idx = prev.indexOf(id);
      if (idx >= 0) {
        return prev.filter((_, i) => i !== idx);
      }
      return [...prev, id];
    });
  }, []);

  const isExpanded = useCallback((id: string): boolean => {
    return expandedIds.includes(id);
  }, [expandedIds]);

  const confirmDelete = useCallback((offender: any) => {
    setDeleteConfirm({ open: true, item: offender });
  }, []);

  const doDelete = useCallback(() => {
    const item = deleteConfirm.item;
    if (item) {
      onChange?.(offenders.filter((o: any) => o.tempid !== item.tempid));
    }
    setDeleteConfirm({ open: false, item: null });
  }, [deleteConfirm.item, offenders, onChange]);

  const cancelDelete = useCallback(() => {
    setDeleteConfirm({ open: false, item: null });
  }, []);

  const getArrestedLabel = useCallback((value: any): string => {
    if (value === 1 || value === '1') return 'Yes';
    if (value === 2 || value === '2') return 'No';
    return 'N/A';
  }, []);

  const getArrestedClass = useCallback((value: any): string => {
    if (value === 1 || value === '1') return 'arrested-yes';
    if (value === 2 || value === '2') return 'arrested-no';
    return 'arrested-na';
  }, []);

  const getOffensesList = useCallback((offender: any): string => {
    if (!offender.offenses || offender.offenses.length === 0) {
      return 'None specified';
    }
    const offenseCodes = Codes.getOffenseCodes();
    const offenseNames: string[] = [];
    for (let i = 0; i < offender.offenses.length; i++) {
      const code = offender.offenses[i];
      const found = offenseCodes.find((oc: any) => oc.code === code || oc.code === parseInt(code));
      if (found) {
        offenseNames.push(found.value);
      } else {
        offenseNames.push(String(code));
      }
    }
    return offenseNames.join(', ');
  }, []);

  const getPersonName = useCallback((offender: any): string => {
    if (offender.personName) {
      return offender.personName;
    }
    if (offender.npsdPerson && involvedPersons) {
      const person = involvedPersons.find((p: any) =>
        p.id === offender.npsdPerson ||
        p.id === Number(offender.npsdPerson)
      );
      if (person) {
        return person.name || person.fullName ||
          `${person.firstName || ''} ${person.lastName || ''}`.trim() ||
          'Unknown';
      }
    }
    return 'Unknown';
  }, [involvedPersons]);

  return (
    <div className={'case-report-offenders-list' + (theme ? ' ' + theme : '')}>
      <div className="header">
        <h1>Offenders</h1>
        {!isReadOnly && (
          <button
            onClick={addOffender}
            className="btn-add"
            type="button"
          >
            <i className="fas fa-plus"></i> OFFENDER
          </button>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '20%' }}>Person</th>
              <th style={{ width: '12%' }}>Arrested</th>
              <th style={{ width: '28%' }}>Offenses</th>
              <th style={{ width: '28%' }}>Notes</th>
              {!isReadOnly && <th style={{ width: '12%' }}>Actions</th>}
            </tr>
          </thead>
          {!hasOffenders ? (
            <tbody>
              <tr>
                <td colSpan={isReadOnly ? 4 : 5} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No offenders added
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {offenders.map((offender) => (
                <tr key={offender.tempid} className="offender-row">
                  <td className="person-name">{getPersonName(offender)}</td>
                  <td>
                    <span className={'arrested-badge ' + getArrestedClass(offender.arrested)}>
                      {getArrestedLabel(offender.arrested)}
                    </span>
                  </td>
                  <td className="offenses-cell">{getOffensesList(offender)}</td>
                  <td className="notes-cell"><span className="multiline-text">{offender.officerNotes || '\u2014'}</span></td>
                  {!isReadOnly && (
                    <td className="actions-cell">
                      <button className="btn-edit" onClick={() => editOffender(offender)}>Edit</button>
                      <button className="btn-delete" onClick={() => confirmDelete(offender)}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="mobile-cards">
        {!hasOffenders ? (
          <div className="empty-state">
            <p>No offenders added</p>
          </div>
        ) : (
          offenders.map((offender) => (
            <div key={'mobile-' + offender.tempid} className="card">
              <div className="card-header">
                <span className="card-person-name">{getPersonName(offender)}</span>
                <span className={'arrested-badge ' + getArrestedClass(offender.arrested)}>
                  Arrested: {getArrestedLabel(offender.arrested)}
                </span>
              </div>

              <div className="card-grid">
                <div className="card-field card-field-full">
                  <div className="card-field-label">Offenses</div>
                  <div className="card-field-value">{getOffensesList(offender)}</div>
                </div>
                <div className="card-field card-field-full">
                  <div className="card-field-label">Notes</div>
                  <div className="card-field-value multiline-text">{offender.officerNotes || '\u2014'}</div>
                </div>
              </div>

              {!isReadOnly && (
                <div className="card-actions">
                  <button className="btn-action btn-edit" onClick={() => editOffender(offender)}>
                    <i className="fa fa-edit"></i> Edit
                  </button>
                  <button className="btn-action btn-delete" onClick={() => confirmDelete(offender)}>
                    <i className="fa fa-trash"></i> Remove
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <ConfirmModal
        open={deleteConfirm.open}
        title="Remove Offender?"
        messageHtml="Are you sure you want to remove this offender from the report?"
        confirmText="Remove"
        cancelText="Cancel"
        confirmClass="danger"
        theme={theme}
        onConfirm={doDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
}

(window as any).CaseReportOffendersListReact = CaseReportOffendersList;
